import { formatDateTime } from '@seen/core/dates'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button } from '@seen/ui'
import { IconRefresh, IconSparkles } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import {
  ExternalTextLink,
  ScrollArea,
  ScrollAreaContent,
} from '#src/components/core/index.ts'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useMetriportRefresh } from '#src/components/external-links/useMetriportRefresh.ts'

export function MetriportLink({
  participant,
}: {
  participant: Participant
}): ReactNode {
  const { refresh, isRefreshing } = useMetriportRefresh(participant.id)

  if (!participant.metriportId) {
    return null
  }

  const hasSummary =
    participant.metriportAiSummary &&
    participant.metriportAiSummary.trim().length > 0
  const lastRefreshed = participant.metriportAiSummaryLastRefreshed

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="relative">
          <ExternalTextLink
            href={`https://dash.metriport.com/patient/${participant.metriportId}`}
          >
            Metriport
          </ExternalTextLink>
        </span>
      </TooltipTrigger>
      <TooltipContent className="w-max max-w-[min(50rem,90vw)]">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <div className="text-sm font-medium">Metriport AI Summary</div>
              <IconSparkles className="size-3" />
            </div>
            <div className="flex items-center gap-2">
              {lastRefreshed && (
                <span className="text-xs text-foreground-tertiary">
                  Last updated {formatDateTime(lastRefreshed)}
                </span>
              )}
              <Button
                variant="ghost"
                size="2xs"
                onClick={refresh}
                loading={isRefreshing}
                className="h-6 px-2 text-xs"
              >
                <IconRefresh className="size-3" />
                Refresh
              </Button>
            </div>
          </div>
          {hasSummary ? (
            <ScrollArea
              scrollRestorationId={`metriport-ai-summary-${participant.id}`}
            >
              <ScrollAreaContent className="max-h-96 px-0 py-2">
                <pre className="text-sm text-foreground-secondary">
                  {participant.metriportAiSummary}
                </pre>
              </ScrollAreaContent>
            </ScrollArea>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="text-sm text-foreground-tertiary">
                AI summary not available
              </div>
              <Button
                variant="outline"
                size="2xs"
                onClick={refresh}
                loading={isRefreshing}
                className="w-fit"
              >
                <IconRefresh className="size-3" />
                Refresh to generate summary
              </Button>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
